import Layout from "@/components/layout/Layout";
import React, { useState } from "react";
import Sidepanel from "@/components/layout/Sidepanel";
import Content from "@/components/layout/Content";
import ShoppingList from "@/components/layout/ShoppingList";
import api from "@/services/api";

/**
 * Shopping Component
 *
 * Main shopping cart page that manages the entire shopping experience.
 * Handles product management, customer information, and order processing
 * with real-time synchronization to the server.
 *
 * @param {Object} props - Component props
 * @param {Array} props.steps - Available navigation steps
 * @param {string} props.step - Current step identifier
 * @param {Object} props.session - Initial session data
 * @param {Array} props.session.products - Products in the cart
 * @param {Object} props.session.contact - Customer contact information
 * @param {string} props.shoppingurl - URL for shopping cart navigation
 * @param {string} props.contactSaveUrl - API endpoint for saving contact information
 * @param {string} props.createNewProductLink - URL for creating new products
 * @param {string} props.saveOrder - API endpoint for placing orders
 * @param {string} props.getContacts - API endpoint for retrieving customer contacts
 * @param {string} props.saveSessionUrl - API endpoint for saving session data
 * @param {Array} props.customers - Available customers (for admin users)
 *
 * @returns {JSX.Element} The rendered shopping cart page
 */
export default function Shopping({
    steps,
    step,
    session,
    shoppingurl,
    contactSaveUrl,
    createNewProductLink,
    saveOrder,
    getContacts,
    saveSessionUrl,
    customers,
}) {
    // Initialize state with session data
    const [sessionlist, setSessionList] = useState(session);
    const [products, setProducts] = useState(session.products);
    const [contact, setContacts] = useState(session.contact);
    const [ses, setSes] = useState(session);
    const [stepList, setStepList] = useState(steps);

    /**
     * Update products array and related session metadata
     * Manages the shopping cart contents and updates product count
     *
     * @param {Array} newproducts - Updated array of products
     */
    function updateProducts(newproducts) {
        setProducts(newproducts);

        // Update session metadata with new product count
        let sess = { ...session };
        sess.products_count = newproducts.length;
        setSessionList(sess);
    }

    /**
     * Update customer contact information with optional server persistence
     * Handles both immediate state updates and server synchronization
     *
     * @param {Object} newcontact - Updated contact information
     * @param {boolean} doRequest - Whether to save to server (default: true)
     */
    function updateContact(newcontact, doRequest = true) {
        setContacts(newcontact);

        // Save to server if requested
        if (doRequest) {
            api.put(contactSaveUrl, { contact: newcontact });
        }
    }

    /**
     * Update session data with optional server persistence
     * Manages order reference and session metadata
     *
     * @param {Object} newses - Updated session data
     * @param {boolean} doRequest - Whether to save to server (default: true)
     */
    function updateSession(newses, doRequest = true) {
        setSes(newses);

        // Save to server if requested
        if (doRequest) {
            api.put(saveSessionUrl, { sessiondata: newses });
        }
    }

    return (
        <Layout>
            <Sidepanel steps={stepList} step={step} />
            <Content
                steps={stepList}
                step={step}
                shoppingurl={shoppingurl}
                session={sessionlist}
            >
                <ShoppingList
                    createNewProductLink={createNewProductLink}
                    contact={contact}
                    products={products}
                    saveOrder={saveOrder}
                    getContacts={getContacts}
                    session={ses}
                    updateSession={updateSession}
                    customers={customers}
                    updateProducts={updateProducts}
                    updateContact={updateContact}
                />
            </Content>
        </Layout>
    );
}
